import { useId, useState } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TimelineIcon from "@mui/icons-material/Timeline";
import {
  Alert,
  Box,
  Button,
  Collapse,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import { VISUALLY_HIDDEN } from "../../theme/visuallyHidden.js";
import type { PdfDownloadOutcome } from "./assessmentPdfDownload.js";
import type {
  FactValue,
  GuestFacts,
  SummaryFacts,
  Told,
} from "./summaryFacts.js";

/** How the views of one assessment write a date. */
export const SHORT_DATE = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

/** A fact's value as the bar shows it, in a comparison saying whether it changed. */
const FactShown = ({ fact }: { fact: FactValue }) => {
  const { t } = useTranslation();
  const valueOf = (value: Told) =>
    value === null
      ? t("assessment.summary.facts.noneRecorded")
      : (value ?? t("assessment.summary.facts.notKnown"));

  return match(fact)
    .with({ kind: "one" }, ({ value }) => <>{valueOf(value)}</>)
    .with({ kind: "both" }, ({ value }) => (
      <>
        {valueOf(value)}{" "}
        <Box component="span" sx={{ color: "text.secondary" }}>
          {t("assessment.summary.facts.both")}
        </Box>
      </>
    ))
    .with({ kind: "moved" }, ({ from, to }) => (
      <>
        {valueOf(from)}
        <Box component="span" aria-hidden sx={{ mx: 0.5 }}>
          →
        </Box>
        <Box component="span" sx={VISUALLY_HIDDEN}>
          {t("assessment.summary.facts.movedTo")}
        </Box>
        <Box component="span" sx={{ fontWeight: 600 }}>
          {valueOf(to)}
        </Box>
      </>
    ))
    .exhaustive();
};

/** Facts listed by label, each label above its value. */
const FactList = ({
  shown,
}: {
  shown: readonly { label: string; fact: FactValue }[];
}) => (
  <Box
    component="dl"
    sx={{
      m: 0,
      px: 2,
      py: 1.5,
      bgcolor: "background.paper",
      border: 1,
      borderColor: "divider",
      borderRadius: 1,
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
      columnGap: 2.5,
      rowGap: 1,
    }}
  >
    {shown.map(({ label, fact }) => (
      <Box key={label} sx={{ minWidth: 0 }}>
        <Typography
          component="dt"
          sx={{ fontSize: "0.6875rem", fontWeight: 700, color: "text.primary" }}
        >
          {label}
        </Typography>
        <Typography component="dd" variant="body2" sx={{ m: 0 }}>
          <FactShown fact={fact} />
        </Typography>
      </Box>
    ))}
  </Box>
);

/** A heading line, with the facts it heads folded away beneath it until asked for. */
const FactsDetails = ({
  heading,
  shown,
}: {
  heading: string;
  shown: readonly { label: string; fact: FactValue }[];
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const factsId = useId();

  return (
    <Box sx={{ px: 3, pt: 1.5 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        useFlexGap
        flexWrap="wrap"
      >
        <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>
          {heading}
        </Typography>
        <Button
          size="small"
          aria-expanded={open}
          aria-controls={factsId}
          onClick={() => {
            setOpen(!open);
          }}
          endIcon={
            <ExpandMoreIcon
              aria-hidden
              sx={{
                transition: "transform 150ms",
                transform: open ? "rotate(180deg)" : "none",
              }}
            />
          }
          sx={{ textTransform: "none", fontWeight: 600, flexShrink: 0 }}
        >
          {open
            ? t("assessment.summary.facts.lessDetails")
            : t("assessment.summary.facts.moreDetails")}
        </Button>
      </Stack>
      <Collapse in={open} id={factsId}>
        <Box sx={{ pt: 1 }}>
          <FactList shown={shown} />
        </Box>
      </Collapse>
    </Box>
  );
};

/**
 * A guest's assessment alone: that it is non-definitive and a guest's, when
 * it was sent and how much of it was answered, then the guest, who asked
 * them, and the instrument.
 */
const GuestFactsDetails = ({
  facts,
  guest,
  sentAt,
}: {
  facts: SummaryFacts;
  guest: GuestFacts;
  sentAt: Date;
}) => {
  const { t } = useTranslation();
  const heading = [
    t("assessment.summary.facts.guest.nonDefinitive"),
    t("assessment.summary.facts.guest.guest"),
    t("assessment.summary.facts.guest.sent", {
      date: SHORT_DATE.format(sentAt),
    }),
    ...(guest.answered === undefined
      ? []
      : [t("assessment.summary.facts.guest.answered", guest.answered)]),
  ].join(" · ");

  return (
    <FactsDetails
      heading={heading}
      shown={[
        {
          label: t("assessment.summary.facts.guest.guest"),
          fact: facts.assessor,
        },
        {
          label: t("assessment.summary.facts.guest.askedBy"),
          fact: { kind: "one", value: guest.askerName },
        },
        {
          label: t("assessment.summary.facts.instrument"),
          fact: facts.instrument,
        },
      ]}
    />
  );
};

/** When the assessment is as of, and who and what it involved; in a comparison, of both. */
const AssessmentFactsDetails = ({ facts }: { facts: SummaryFacts }) => {
  const { t } = useTranslation();
  const [first, second] = facts.moments;
  const latest = second ?? first;
  const dates =
    second === undefined
      ? SHORT_DATE.format(first.at)
      : t("assessment.summary.facts.dates", {
          first: SHORT_DATE.format(first.at),
          second: SHORT_DATE.format(second.at),
        });
  const { guest } = facts;

  if (guest !== undefined) {
    return <GuestFactsDetails facts={facts} guest={guest} sentAt={first.at} />;
  }

  const shown: { label: string; fact: FactValue }[] = [
    { label: t("assessment.summary.facts.placement"), fact: facts.placement },
    { label: t("assessment.summary.facts.assessor"), fact: facts.assessor },
    ...(facts.approver === undefined
      ? []
      : [
          {
            label: t("assessment.summary.facts.approver"),
            fact: facts.approver,
          },
        ]),
    { label: t("assessment.summary.facts.instrument"), fact: facts.instrument },
  ];

  return (
    <FactsDetails
      heading={t(`assessment.summary.facts.at.${latest.kind}`, { dates })}
      shown={shown}
    />
  );
};

const TAB_STYLE = {
  minHeight: 40,
  py: 1,
  px: 1.5,
  fontSize: "0.875rem",
  textTransform: "none",
  fontWeight: 600,
  color: "text.secondary",
  "&.Mui-selected": { color: "text.primary", fontWeight: 700 },
} as const;

/** Which view of an assessment is open, and the address of the other. */
export type AssessmentView =
  { kind: "summary"; fullHref: string } | { kind: "full"; summaryHref: string };

/**
 * What heads every view of one assessment: its facts, the tabs between its
 * summary and its every answer, and the way to the young person's history.
 *
 * The open view's tab is not a link, since following it would lose whatever
 * that view's address holds beyond the assessment.
 */
export const AssessmentViewHeader = ({
  facts,
  view,
  historyHref,
  onDownloadPdf,
}: {
  /** Absent where there is nothing yet to say. */
  facts?: SummaryFacts | undefined;
  view: AssessmentView;
  /** Absent where the history is not offered. */
  historyHref?: string | undefined;
  /** Absent where the assessment is not offered as a PDF. */
  onDownloadPdf?: (() => Promisable<PdfDownloadOutcome>) | undefined;
}) => {
  const { t } = useTranslation();
  const linkTo = (href: string) => ({ component: RouterLink, to: href });
  const [downloading, setDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);

  return (
    <>
      <Box
        sx={{
          bgcolor: "grey.100",
          border: 1,
          borderColor: "divider",
          borderRadius: "4px 4px 0 0",
        }}
      >
        {facts === undefined ? null : <AssessmentFactsDetails facts={facts} />}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ px: 3, pt: 1 }}
        >
          <Tabs
            value={view.kind}
            aria-label={t("assessment.summary.tabs.label")}
            sx={{
              minHeight: 40,
              flexGrow: 1,
              "& .MuiTabs-indicator": { height: 3 },
            }}
          >
            <Tab
              value="summary"
              label={t("assessment.summary.tabs.summary")}
              sx={TAB_STYLE}
              {...(view.kind === "full" ? linkTo(view.summaryHref) : {})}
            />
            <Tab
              value="full"
              label={t("assessment.summary.tabs.full")}
              sx={TAB_STYLE}
              {...(view.kind === "summary" ? linkTo(view.fullHref) : {})}
            />
          </Tabs>
          {historyHref === undefined ? null : (
            <Button
              size="small"
              variant="outlined"
              component={RouterLink}
              to={historyHref}
              startIcon={<TimelineIcon />}
              sx={{ textTransform: "none", flexShrink: 0 }}
            >
              {t("assessment.summary.tabs.history")}
            </Button>
          )}
          {onDownloadPdf === undefined ? null : (
            <Button
              size="small"
              variant="outlined"
              disabled={downloading}
              startIcon={<DownloadIcon />}
              sx={{ textTransform: "none", flexShrink: 0 }}
              onClick={() => {
                setDownloading(true);
                setDownloadFailed(false);
                void Promise.resolve()
                  .then(onDownloadPdf)
                  .catch((): PdfDownloadOutcome => "failed")
                  .then((outcome) => {
                    setDownloading(false);
                    setDownloadFailed(outcome === "failed");
                  });
              }}
            >
              {t("assessment.summary.tabs.downloadPdf")}
            </Button>
          )}
        </Stack>
      </Box>
      {downloadFailed ? (
        <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
          {t("assessment.summary.tabs.downloadPdfFailed")}
        </Alert>
      ) : null}
    </>
  );
};
